import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowBackIos, ArrowForwardIos, TrendingUp } from '@mui/icons-material';
import { Avatar, Button, Radio } from '@mui/material';
import useExplorePageStyles from '../useExplorePageStyles';
import useGlobalStyles from '../../../useGlobalStyles';
import usePageLink from '../../../../hooks/useQuestion';
import { AnswerWithQid, Question, User } from '../../../../types';
import { ModelType, setTrendType } from '../../../../redux/slices/exploreSlice';
import useTrendingStyles from './useTrendingStyles';
import {
  getAnswersWithScore,
  getQuestionsWithScore,
  getUsersWithUpdatedScore,
} from '../exploreUtils';

/**
 * Interface representing the props for the Trending component.
 *
 * - users The current full list of users.
 * - questions The current full list of questions.
 * - answers The current full list of answers with their question's IDs.
 * - trendType The current trend type that the trending tab is displaying.
 */
interface TrendingProps {
  users: User[];
  questions: Question[];
  answers: AnswerWithQid[];
  trendType: ModelType;
}

/**
 * Trending component renders a trending tab on the explore page which can display
 * trending users, questions, and answers.
 *
 * @param users - The current full list of users.
 * @param questions - The current full list of questions.
 * @param answers - The current full list of answers with their question's IDs.
 * @param trendType - The current trend type that the trending tab is displaying.
 */
const Trending = ({ users, questions, answers, trendType }: TrendingProps) => {
  const { classes } = useExplorePageStyles();
  const { classes: trendingClasses } = useTrendingStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const { handleAnswer } = usePageLink();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const progressBarPercent = `${(progress / 2500) * 100}%`;

  const trendingUser = getUsersWithUpdatedScore(users, questions, answers, 'trending').toSorted(
    (a, b) => b.score - a.score,
  )[0];
  const trendingQuestion = getQuestionsWithScore(questions, 'trending').toSorted(
    (a, b) => b.score - a.score,
  )[0];
  const trendingAnswer = getAnswersWithScore(answers, 'trending').toSorted(
    (a, b) => b.score - a.score,
  )[0];

  /**
   * Function to return the background image based on the current trend type.
   *
   * @returns a background image.
   */
  const backgroundImage = () => {
    if (trendType === ModelType.UserT) return 'url(assets/trendbackgrounds/user.png)';
    if (trendType === ModelType.QuestionT) return 'url(assets/trendbackgrounds/question.png)';
    if (trendType === ModelType.AnswerT) return 'url(assets/trendbackgrounds/answer.png)';
    return '';
  };

  /**
   * Function to set the trend type to the given ModelType.
   *
   * @param type - The ModelType to set the trend type to.
   */
  const handleChangeType = (type: ModelType) => {
    dispatch(setTrendType(type));
  };

  /**
   * Function to switch the trend type, moving forwards through the list of ModelTypes.
   */
  const handleMoveForward = useCallback(() => {
    if (trendType === ModelType.UserT) dispatch(setTrendType(ModelType.QuestionT));
    if (trendType === ModelType.QuestionT) dispatch(setTrendType(ModelType.AnswerT));
    if (trendType === ModelType.AnswerT) dispatch(setTrendType(ModelType.UserT));
  }, [dispatch, trendType]);

  /**
   * Function to switch the trend type, moving backwards through the list of ModelTypes.
   */
  const handleMoveBackward = () => {
    if (trendType === ModelType.UserT) dispatch(setTrendType(ModelType.AnswerT));
    if (trendType === ModelType.QuestionT) dispatch(setTrendType(ModelType.UserT));
    if (trendType === ModelType.AnswerT) dispatch(setTrendType(ModelType.QuestionT));
  };

  useEffect(() => {
    // Switches the ModelType moving forwards every 2500 ticks without user input
    const progInterval = setInterval(() => {
      setProgress(prevProgress => {
        if (prevProgress >= 2500) {
          handleMoveForward();
          return 0;
        }
        return prevProgress + 1;
      });
    }, 1);

    return () => clearInterval(progInterval);
  }, [handleMoveForward]);

  return (
    <div className={trendingClasses.trendingContainerRow}>
      <div
        className={trendingClasses.trendingContainerColumn}
        style={{ backgroundImage: backgroundImage() }}>
        <div className={classes.titleContainer}>
          <span>Trending</span>
          <TrendingUp />
        </div>
        {trendType === ModelType.UserT && (
          <div className={trendingClasses.trendContainer}>
            <div className={trendingClasses.trendingContent}>
              <span>Trending User:</span>
            </div>
            <div className={trendingClasses.trendingContent}>
              <Avatar
                className={globalClasses.userAvatar}
                src={`${process.env.PUBLIC_URL}/assets/profiles/${trendingUser?.profilePicture}`}
              />
              <span>{trendingUser?.username}</span>
            </div>
            <Button
              className={globalClasses.button}
              onClick={() => navigate(`/profile/${trendingUser?.username}`)}>
              Go to User
            </Button>
          </div>
        )}
        {trendType === ModelType.QuestionT && (
          <div className={trendingClasses.trendContainer}>
            <div className={trendingClasses.trendingContent}>
              <span>Trending Question:</span>
            </div>
            <div className={`${trendingClasses.trendingContent} ${trendingClasses.trendingQA}`}>
              <span>{trendingQuestion?.title}</span>
            </div>
            <Button
              className={globalClasses.button}
              onClick={() => {
                if (trendingQuestion._id) {
                  handleAnswer(trendingQuestion._id);
                }
              }}>
              Go to Question
            </Button>
          </div>
        )}
        {trendType === ModelType.AnswerT && (
          <div className={trendingClasses.trendContainer}>
            <div className={trendingClasses.trendingContent}>
              <span>Trending Answer:</span>
            </div>
            <div className={`${trendingClasses.trendingContent} ${trendingClasses.trendingQA}`}>
              <span>{trendingAnswer.text}</span>
            </div>
            <Button
              className={globalClasses.button}
              onClick={() => {
                if (trendingAnswer.qid) {
                  handleAnswer(trendingAnswer.qid);
                }
              }}>
              Go to Answer
            </Button>
          </div>
        )}
        <div className={trendingClasses.trendingNav}>
          <div className={trendingClasses.trendingNavButtonsContainer}>
            <ArrowBackIos
              className={trendingClasses.trendingArrowButton}
              onClick={() => handleMoveBackward()}
            />
            <Radio
              checked={trendType === ModelType.UserT}
              onChange={() => handleChangeType(ModelType.UserT)}
              value='user'
              name='radio-buttons'
              inputProps={{ 'aria-label': 'User' }}
            />
            <Radio
              checked={trendType === ModelType.QuestionT}
              onChange={() => handleChangeType(ModelType.QuestionT)}
              value='question'
              name='radio-buttons'
              inputProps={{ 'aria-label': 'Question' }}
            />
            <Radio
              checked={trendType === ModelType.AnswerT}
              onChange={() => handleChangeType(ModelType.AnswerT)}
              value='answer'
              name='radio-buttons'
              inputProps={{ 'aria-label': 'Answer' }}
            />
            <ArrowForwardIos
              className={trendingClasses.trendingArrowButton}
              onClick={() => handleMoveForward()}
            />
          </div>
          <div className={trendingClasses.progressBarContainer}>
            <div
              className={trendingClasses.progressBar}
              style={{ width: progressBarPercent }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trending;
